/**
 * 认证上下文 - 管理全局认证状态
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, LoginRequest } from '../types';
import { login as apiLogin, getCurrentUser } from '../api/auth';
import { saveToken, saveUser, clearAuth, getToken, getUser } from '../utils/auth';
import { message } from 'antd';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * 认证 Provider
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 初始化 - 从本地存储恢复用户信息
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      const cachedUser = getUser();

      if (token && cachedUser) {
        setUser(cachedUser);
        
        // 尝试刷新用户信息
        try {
          const freshUser = await getCurrentUser();
          setUser(freshUser);
          saveUser(freshUser);
        } catch (error) {
          // 如果刷新失败，清除认证信息
          console.error('刷新用户信息失败:', error);
          clearAuth();
          setUser(null);
        }
      }

      setLoading(false);
    };

    initAuth();
  }, []);

  /**
   * 登录
   */
  const login = async (data: LoginRequest) => {
    try {
      const response = await apiLogin(data);
      
      if (response.success && response.data) {
        const { token, user: userData } = response.data;
        
        // 保存 token 和用户信息
        saveToken(token);
        saveUser(userData);
        setUser(userData);
        
        message.success('登录成功！');
      } else {
        throw new Error('登录失败');
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.errors?.[0]?.message || '登录失败';
      message.error(errorMsg);
      throw error;
    }
  };

  /**
   * 登出
   */
  const logout = () => {
    clearAuth();
    setUser(null);
    message.success('已退出登录');
  };

  /**
   * 刷新用户信息
   */
  const refreshUser = async () => {
    try {
      const freshUser = await getCurrentUser();
      setUser(freshUser);
      saveUser(freshUser);
    } catch (error) {
      console.error('刷新用户信息失败:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * 使用认证上下文的 Hook
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

